import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import UserAccountPage from './pages/UserAccountPage';
import AdminDashboard from './pages/AdminDashboard';
import WalletPage from './pages/WalletPage';
import CompleteProfile from './pages/CompleteProfile';
import InstallApp from './components/InstallApp';
import InstallButton from './components/InstallButton';
import CheckoutPage from './pages/CheckoutPage';
import CafeTables from './pages/CafeTables';
import RestaurantMenu from './pages/RestaurantMenu';
import NotFound from './pages/NotFound'; // Import your NotFound component
import OrderConfirmation from './pages/OrderConfirmation';
import OwnerMenuEdit from './pages/OwnerMenuEdit';
import NavBar from './components/NavBar';
import { CacheProvider } from '@emotion/react';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AdminMenuEdit from './pages/AdminMenuEdit';
import ShopOwnerRequest from './pages/ShopOwnerRequest';
import DeveloperDocs from './pages/DeveloperDocs';
import PrivacyPage from './pages/PrivacyPage';
import ShareRestaurant from './pages/ShareRestaurant';
import TourGuidePage from './pages/TourGuidePage';
import AuthSuccess from './components/AuthSuccess';
import './styles/App.css';
import ProductDetailPage from './pages/ProductDetailPage';
import AdminUsers from './pages/AdminUsers';
import AdminOrdersPage from './pages/OrdersPage';
import AdminPrinters from './pages/AdminPrinters';
import PaymentSettings from './pages/PaymentSettings';
import OrderDetails from './pages/OrderDetails';
import ProtectedRoute from './components/ProtectedRoute';
import PublicRouter from './components/PublicRouter';
import ProtectedRouteWithoutCompletionCheck from './components/ProtectedRouteWithoutCompletionCheck';
import { CartProvider } from './context/CartContext';
import CartDrawer from './components/CartDrawer';
import { NotificationProvider } from './context/NotificationContext';
import { ConfirmProvider } from 'material-ui-confirm';
import { blue } from "@mui/material/colors";
import { arEG } from "@mui/material/locale"; // Arabic MUI support
import WelcomePopup from './components/WelcomePopup';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';
import createCache from '@emotion/cache';
import AboutPage from './pages/AboutPage';
import PrintServiceClient from './pages/PrintServiceClient';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { ChatProvider } from './context/ChatContext';
import ChatWidget from './components/ChatWidget';
import { AdminChatProvider } from './context/AdminChatContext';
import AdminChatPanel from './pages/AdminChatPanel';
import keywordsData from './lang/keywords.json';
import GlobalLoadingDialog from './components/GlobalLoadingDialog';
import GlobalStyles from './components/GlobalStyles';
import UserOrdersListPage from './pages/UserOrdersListPage';
import LandingPage from './pages/LandingPage';
import StoreDetailPage from './pages/StoreDetailPage';
import CookieConsent from './components/CookieConsent';

const App = () => (
  <LanguageProvider>
      <MainApp />
  </LanguageProvider>
);

const MainApp = () => {
    const { language,toggleLanguage } = useLanguage();
  //const lang = language === 'ar' ? 'ar-IQ' : 'en-US';

  const rtlCache = createCache({
    key: language === 'ar' ? 'muirtl' : 'mui',
    stylisPlugins: language === 'ar' ? [prefixer, rtlPlugin] : [],
  });
// Modern color palette
const colors = {
  primary: {
    main: '#FFD700',
    light: '#FFE55C',
    dark: '#DAA520',
    contrastText: '#000000',
  },
  secondary: {
    main: '#1A1A1A',
    light: '#2D2D2D',
    dark: '#0D0D0D',
    contrastText: '#FFFFFF',
  },
  background: {
    default: '#000000',
    paper: '#0D0D0D',
    card: '#1A1A1A',
  },
  text: {
    primary: '#FFFFFF',
    secondary: '#B3B3B3',
    disabled: '#6B6B6B',
  },
  success: {
    main: '#4CAF50',
    light: '#81C784',
    dark: '#388E3C',
  },
  error: {
    main: '#FF3D00',
    light: '#FF6B3D',
    dark: '#D32F2F',
  },
  warning: {
    main: '#FF9800',
    light: '#FFB74D',
    dark: '#F57C00',
  },
  info: {
    main: '#2196F3',
    light: '#64B5F6',
    dark: '#1976D2',
  },
};

   const theme = createTheme({
    direction: language === 'ar' ? 'rtl' : 'ltr',
    palette: {
      mode: 'dark',
      primary: colors.primary,
      secondary: colors.secondary,
      background: {
        default: colors.background.default,
        paper: colors.background.paper,
      },
      text: colors.text,
      success: colors.success,
      error: colors.error,
      warning: colors.warning,
      info: colors.info,
    },
    typography: {
      fontFamily: "'Cairo', 'Poppins', 'Segoe UI', sans-serif",
      h1: {
        fontWeight: 800,
        fontSize: '3.5rem',
        letterSpacing: '-0.02em',
        background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.light} 100%)`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
      },
      h2: {
        fontWeight: 700,
        fontSize: '2.5rem',
        letterSpacing: '-0.01em',
      },
      h3: {
        fontWeight: 700,
        fontSize: '2rem',
      },
      h4: {
        fontWeight: 700,
        fontSize: '1.75rem',
      },
      h5: {
        fontWeight: 600,
        fontSize: '1.5rem',
      },
      h6: {
        fontWeight: 600,
        fontSize: '1.25rem',
      },
      subtitle1: {
        fontWeight: 500,
        fontSize: '1rem',
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.6,
      },
      button: {
        textTransform: 'none',
        fontWeight: 600,
        letterSpacing: '0.02em',
      },
    },
    shape: {
      borderRadius: 16,
    },
    shadows: [
      'none',
      '0 2px 4px rgba(0,0,0,0.1)',
      '0 4px 8px rgba(0,0,0,0.12)',
      '0 8px 16px rgba(0,0,0,0.14)',
      '0 12px 24px rgba(0,0,0,0.16)',
      '0 16px 32px rgba(0,0,0,0.18)',
      '0 20px 40px rgba(0,0,0,0.2)',
      '0 24px 48px rgba(0,0,0,0.22)',
      '0 28px 56px rgba(0,0,0,0.24)',
      '0 32px 64px rgba(0,0,0,0.26)',
      '0 36px 72px rgba(0,0,0,0.28)',
      '0 40px 80px rgba(0,0,0,0.3)',
      '0 44px 88px rgba(0,0,0,0.32)',
      '0 48px 96px rgba(0,0,0,0.34)',
      '0 52px 104px rgba(0,0,0,0.36)',
      '0 56px 112px rgba(0,0,0,0.38)',
      '0 60px 120px rgba(0,0,0,0.4)',
      '0 64px 128px rgba(0,0,0,0.42)',
      '0 68px 136px rgba(0,0,0,0.44)',
      '0 72px 144px rgba(0,0,0,0.46)',
      '0 76px 152px rgba(0,0,0,0.48)',
      '0 80px 160px rgba(0,0,0,0.5)',
      '0 84px 168px rgba(0,0,0,0.52)',
      '0 88px 176px rgba(0,0,0,0.54)',
      '0 92px 184px rgba(0,0,0,0.56)',
    ],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '@global': {
            '*': {
              scrollbarWidth: 'thin',
              scrollbarColor: `${colors.primary.main} ${colors.background.card}`,
            },
            '::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '::-webkit-scrollbar-track': {
              background: colors.background.card,
              borderRadius: '10px',
            },
            '::-webkit-scrollbar-thumb': {
              background: colors.primary.main,
              borderRadius: '10px',
              '&:hover': {
                background: colors.primary.light,
              },
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            padding: '10px 24px',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
            },
          },
          contained: {
            background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.light} 100%)`,
            color: '#000000',
            fontWeight: 700,
            '&:hover': {
              background: `linear-gradient(135deg, ${colors.primary.light} 0%, ${colors.primary.main} 100%)`,
              boxShadow: `0 8px 20px ${alpha(colors.primary.main, 0.3)}`,
            },
          },
          outlined: {
            borderColor: colors.primary.main,
            color: colors.primary.main,
            '&:hover': {
              borderColor: colors.primary.light,
              backgroundColor: alpha(colors.primary.main, 0.1),
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: colors.background.card,
            borderRadius: '20px',
            border: `1px solid ${alpha(colors.primary.main, 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              borderColor: colors.primary.main,
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 32px ${alpha(colors.primary.main, 0.15)}`,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            transition: 'all 0.3s ease',
          },
          elevation1: {
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          },
          elevation2: {
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          },
          elevation3: {
            boxShadow: '0 12px 36px rgba(0,0,0,0.14)',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              transition: 'all 0.3s ease',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.primary.main,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.primary.main,
                borderWidth: '2px',
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            fontWeight: 600,
            transition: 'all 0.3s ease',
          },
          colorPrimary: {
            backgroundColor: colors.primary.main,
            color: '#000000',
          },
          colorSecondary: {
            backgroundColor: colors.secondary.light,
            color: '#FFFFFF',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: '24px',
            background: `linear-gradient(135deg, ${colors.background.paper} 0%, ${colors.background.card} 100%)`,
            border: `1px solid ${alpha(colors.primary.main, 0.2)}`,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            border: '1px solid',
          },
          standardSuccess: {
            backgroundColor: alpha(colors.success.main, 0.1),
            borderColor: colors.success.main,
            color: colors.success.light,
          },
          standardError: {
            backgroundColor: alpha(colors.error.main, 0.1),
            borderColor: colors.error.main,
            color: colors.error.light,
          },
          standardWarning: {
            backgroundColor: alpha(colors.warning.main, 0.1),
            borderColor: colors.warning.main,
            color: colors.warning.light,
          },
          standardInfo: {
            backgroundColor: alpha(colors.info.main, 0.1),
            borderColor: colors.info.main,
            color: colors.info.light,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: colors.background.card,
            '& .MuiTableCell-root': {
              fontWeight: 700,
              color: colors.primary.main,
              borderBottom: `2px solid ${alpha(colors.primary.main, 0.2)}`,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${alpha(colors.primary.main, 0.1)}`,
          },
        },
      },
      MuiBottomNavigation: {
        styleOverrides: {
          root: {
            background: `linear-gradient(180deg, ${colors.background.paper} 0%, ${colors.background.card} 100%)`,
            borderTop: `2px solid ${colors.primary.main}`,
            backdropFilter: 'blur(10px)',
          },
        },
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          root: {
            color: colors.text.secondary,
            transition: 'all 0.3s ease',
            '&.Mui-selected': {
              color: colors.primary.main,
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.75rem',
                fontWeight: 700,
              },
            },
            '&:hover': {
              color: colors.primary.main,
              transform: 'translateY(-2px)',
            },
          },
        },
      },
    },
  });
  
// Global Theme Setup
const blackYellowTheme = {
  direction: language === 'ar' ? 'rtl' : 'ltr',
  palette: {
    mode: 'dark',
    primary: {
      main: '#000000',        // Pure black
      light: '#1A1A1A',       // Soft black
      dark: '#000000',        // Deep black
      contrastText: '#ffbb00', // Yellow text on black
    },
    secondary: {
      main: '#ffbb00',        // Bright yellow
      light: '#FFE55C',       // Soft yellow
      dark: '#DAA520',        // Deep gold
      contrastText: '#000000', // Black text on yellow
    },
    background: {
      default: '#000000',     // Pure black background
      paper: '#0D0D0D',       // Matte black cards
      accent: '#1A1A1A',      // Soft black for secondary areas
    },
    text: {
      primary: '#FFFFFF',     // White text
      secondary: '#B3B3B3',   // Light gray text
      disabled: '#6B6B6B',    // Disabled gray text
      hint: '#FFD700',        // Yellow hint text
    },
    action: {
      active: '#ffbb00',      // Yellow active
      hover: '#FFE55C',       // Soft yellow hover
      selected: '#DAA520',    // Deep gold selected
      disabled: '#4A4A4A',    // Gray disabled
      disabledBackground: '#2D2D2D',
    },
    divider: '#2D2D2D',       // Dark gray dividers
  },
  typography: {
    fontFamily: '"Poppins", "Cairo", "Arial", sans-serif',
    h4: {
      color: '#ffbb00',       // Yellow headings
      fontWeight: 700,
      letterSpacing: '1px',
    },
    h6: {
      color: '#FFFFFF',       // White subheadings
      fontWeight: 600,
    },
    body1: {
      color: '#FFFFFF',
    },
    body2: {
      color: '#B3B3B3',
    },
  },
  shape: {
    borderRadius: 12,
  },
};

//const theme = createTheme(blackYellowTheme);


  return (
    <CacheProvider value={rtlCache}>
        <ThemeProvider theme={theme}>
          <div translate="no" className="app" dir={language === "ar" ? "rtl" : "ltr"}>
          <CssBaseline />
          <GlobalStyles/>
          <InstallApp language={language} />
          <NotificationProvider>
          <CartProvider>    
          <NavBar keywordsData={keywordsData} language={language} toggleLanguage={toggleLanguage}/>
          <ConfirmProvider>
          <ChatProvider>
          <AdminChatProvider>
          <GlobalLoadingDialog language={language} />
          <WelcomePopup isPart={false}/>
          <InstallButton language={language} />
          <CookieConsent language={language} />
          <Routes>
            <Route path="/admin/chats" element={<ProtectedRoute admin><AdminChatPanel /></ProtectedRoute>} />
            <Route path="/super/chats" element={<ProtectedRoute superAdmin><AdminChatPanel /></ProtectedRoute>} />
            <Route path="/" element={<HomePage keywordsData={keywordsData} language={language}/>} />
            <Route path="/:lang" element={<HomePage keywordsData={keywordsData} language={language}/>} />
            <Route path="/login" element={<PublicRouter><LoginPage keywordsData={keywordsData} language={language}/></PublicRouter>} />
            <Route path="/tour" element={<TourGuidePage keywordsData={keywordsData} language={language}/>} />
            <Route
  path="/tour/places/:id"
  element={<StoreDetailPage keywordsData={keywordsData} language={language} />}
/>
            <Route path="/about" element={<AboutPage keywordsData={keywordsData} language={language}/>} />
            <Route
  path="/privacy"
  element={<PrivacyPage keywordsData={keywordsData} language={language} />}
/>
            <Route path="/register" element={<PublicRouter><RegisterPage keywordsData={keywordsData} language={language}/></PublicRouter>} />
            <Route path="/product/:id" element={<ProductDetailPage keywordsData={keywordsData} language={language}/>} />
            <Route path="/account" element={<ProtectedRoute><UserAccountPage keywordsData={keywordsData} language={language}/></ProtectedRoute>} />
            <Route path="/wallet" element={<ProtectedRoute><WalletPage keywordsData={keywordsData} language={language}/></ProtectedRoute>} />
            <Route path="/checkout" element={<CheckoutPage keywordsData={keywordsData} language={language}/>} />
            <Route path="/order-confirmation/:orderId" element={<OrderConfirmation keywordsData={keywordsData} language={language}/>} />
            <Route path="/admin" element={<ProtectedRoute admin><AdminDashboard keywordsData={keywordsData} language={language}/></ProtectedRoute>} />
            <Route path="/admin/adminprinters" element={<ProtectedRoute admin><AdminPrinters keywordsData={keywordsData} language={language}/></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute admin><AdminUsers keywordsData={keywordsData} language={language}/></ProtectedRoute>} />
            <Route path="/admin/orders" element={<ProtectedRoute admin><AdminOrdersPage keywordsData={keywordsData} language={language}/></ProtectedRoute>} />
            <Route path="/admin/cafetables" element={<ProtectedRoute admin><CafeTables keywordsData={keywordsData} language={language}/></ProtectedRoute>} />
            <Route path="/admin/paymentsettings" element={<ProtectedRoute admin><PaymentSettings keywordsData={keywordsData} language={language}/></ProtectedRoute>} />
            <Route path="/admin/orders/:orderId" element={<ProtectedRoute admin><OrderDetails keywordsData={keywordsData} language={language}/></ProtectedRoute>} />
            <Route path="/casher/orders" element={<ProtectedRoute casher><AdminOrdersPage keywordsData={keywordsData} language={language}/></ProtectedRoute>} />
            <Route path="/casher/orders/:orderId" element={<ProtectedRoute casher><OrderDetails keywordsData={keywordsData} language={language}/></ProtectedRoute>} />
            <Route path="/auth/success" element={<AuthSuccess keywordsData={keywordsData} language={language}/>} />
            <Route path="/complete-profile" element={<ProtectedRouteWithoutCompletionCheck><CompleteProfile keywordsData={keywordsData} language={language}/></ProtectedRouteWithoutCompletionCheck>} />
            <Route path="/forgot-password" element={<PublicRouter><ForgotPassword keywordsData={keywordsData} language={language}/></PublicRouter>} />
            <Route path="/reset-password/:token" element={<PublicRouter><ResetPassword keywordsData={keywordsData} language={language}/></PublicRouter>} />
            <Route path="/restaurant/:id" element={<RestaurantMenu keywordsData={keywordsData} language={language} />} />
            <Route path="/restaurant/:id/:lang" element={<RestaurantMenu keywordsData={keywordsData} language={language} />} />
            <Route path="/t/:id/:tableId" element={<RestaurantMenu keywordsData={keywordsData} language={language}/>} />
            <Route path='/shop-owner-request' element={<ProtectedRoute userRole><ShopOwnerRequest keywordsData={keywordsData} language={language} /></ProtectedRoute>}/>
            <Route path='/super' element={<ProtectedRoute superAdmin><AdminMenuEdit keywordsData={keywordsData} language={language} /></ProtectedRoute>}/>
            <Route path='/admin/shop-owner-menu' element={<ProtectedRoute admin><OwnerMenuEdit keywordsData={keywordsData} language={language} /></ProtectedRoute>}/>
            <Route path="/admin/adminprinters/client" element={<ProtectedRoute admin><PrintServiceClient keywordsData={keywordsData} language={language} /></ProtectedRoute>} /> 
            <Route path="/share/restaurant/:restaurantId" element={<ShareRestaurant />} />
            <Route path="/admin/developer-docs" element={<ProtectedRoute admin><DeveloperDocs language={language} /></ProtectedRoute>} />
            <Route path="/my-orders" element={<UserOrdersListPage language={language} />} />
            <Route path="*" element={<NotFound keywordsData={keywordsData} language={language} />} /> {/* Catch-all route */} 
          </Routes>
          <ChatWidget />
          </AdminChatProvider>
          </ChatProvider>
          </ConfirmProvider>
      <CartDrawer />
      </CartProvider>
      </NotificationProvider>
      </div>
    </ThemeProvider>
    </CacheProvider>
  );
}

export default App;